import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import useControlledState from '@quid/react-use-controlled-state';
import { default as RcTooltip } from 'rc-tooltip';
import { cloneElement, forwardRef, isValidElement, useEffect, useMemo, useRef, } from 'react';
import { useMergeRefs } from 'use-callback-ref';
import { OverflowContext } from './OverflowContext.js';
import { OverflowTrigger } from './OverflowTrigger.js';
import { TooltipPaper } from './Paper.js';
import { builtinPlacements } from './placements.js';
import { Global } from './Tooltip.styles.js';
import { uuid } from './uuid.js';
// eslint-disable-next-line react/display-name
const TooltipCompoundParent = forwardRef(({ title, visible, onVisibleChange, maxWidth, children, dataTestSpecifier, animation = 'zoom', placement = 'bottom', ...rest }, ref) => {
    const [visibleControlled, onVisibleChangeControlled] = useControlledState(visible === undefined ? false : undefined, visible, onVisibleChange);
    const { current: overflowTriggers } = useRef(new Set());
    const childrenRef = useRef();
    const tooltipId = useMemo(uuid, []);
    const handleVisibleChange = (value) => {
        if (value) {
            const noOverflowTriggers = overflowTriggers.size === 0;
            const hasOverflowTriggerCut = Array.from(overflowTriggers).some((isOverflowActive) => isOverflowActive());
            if (noOverflowTriggers || hasOverflowTriggerCut) {
                onVisibleChangeControlled(value);
            }
        }
        else {
            onVisibleChangeControlled(false);
        }
    };
    if (!isValidElement(children)) {
        throw new Error('Tooltip accepts single element as a child');
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore TODO try with useImperativeHandle, also find way to get ref in TS
    const mergedRef = useMergeRefs([ref, children?.ref, childrenRef]);
    useEffect(() => {
        childrenRef.current?.setAttribute('data-trigger-id', tooltipId);
    }, [childrenRef, tooltipId]);
    return (_jsxs(_Fragment, { children: [_jsx(Global, { id: "global-tooltip-styles" }), _jsx(OverflowContext.Provider, { value: overflowTriggers, children: _jsx(RcTooltip, { visible: !!title && visibleControlled, onVisibleChange: handleVisibleChange, mouseEnterDelay: 0.3, animation: animation, placement: placement, ...rest, id: tooltipId, overlay: _jsx(TooltipPaper, { maxWidth: maxWidth, "data-test-specifier": dataTestSpecifier, children: title }), builtinPlacements: builtinPlacements, prefixCls: "cld-tooltip", children: cloneElement(children, {
                        // @ts-expect-error TODO: Fix types
                        ref: mergedRef,
                    }) }) })] }));
});
export const Tooltip = Object.assign(TooltipCompoundParent, {
    OverflowTrigger,
});
